import axios, { AxiosInstance, AxiosRequestConfig, AxiosResponse } from "axios";

const AUTH_STORAGE_KEYS = ["harenaCowonder", "harenaCowonderAuth", "authToken"] as const;

type StoredAuthLike = {
  auth?: string;
  token?: string;
  accessToken?: string;
};

function readStoredAuthPayload(): StoredAuthLike | null {
  for (const key of AUTH_STORAGE_KEYS) {
    const raw = localStorage.getItem(key);
    if (!raw) continue;

    try {
      const parsed = JSON.parse(raw);
      if (typeof parsed === "string") return { auth: parsed };
      if (parsed && typeof parsed === "object") return parsed as StoredAuthLike;
    } catch {
      return { auth: raw };
    }
  }

  return null;
}

function getStoredToken(): string | null {
  try {
    const stored = readStoredAuthPayload();
    if (!stored) return null;
    if (stored.auth) return String(stored.auth);
    if (stored.token) return String(stored.token);
    if (stored.accessToken) return String(stored.accessToken);
    return null;
  } catch {
    return null;
  }
}

function clearStoredAuth() {
  for (const key of AUTH_STORAGE_KEYS) {
    localStorage.removeItem(key);
  }
}

function isAuthEndpoint(url: string | undefined) {
  if (!url) return false;
  return /\/login|\/signin|\/auth|\/token|\/refresh/gi.test(url);
}

function isTokenExpired(token: string | null) {
  if (!token) return false;
  try {
    const parts = token.split(".");
    if (parts.length < 2) return false;
    const payload = JSON.parse(window.atob(parts[1]));
    if (!payload?.exp) return false;
    return Date.now() >= payload.exp * 1000;
  } catch {
    return false;
  }
}

function hasAuthorizationHeader(headers: AxiosRequestConfig["headers"]) {
  if (!headers) return false;
  const asRecord = headers as Record<string, unknown>;
  return Boolean(asRecord.Authorization || asRecord.authorization);
}

export const noAuthApi = axios.create({
  baseURL: import.meta.env.VITE_COWONDER_BACK_BASE_URL || "http://localhost:4300",
});

class ApiConnector {
  private axiosInstance: AxiosInstance;

  constructor() {
    this.axiosInstance = axios.create({
      baseURL:
        import.meta.env.VITE_COWONDER_BACK_BASE_URL || "http://localhost:4300",
      headers: {
        "Content-Type": "application/json",
        "ngrok-skip-browser-warning": "true",
      },
    });

    this.axiosInstance.interceptors.request.use(
      (config) => {
        try {
          if ((config as any)?.skipAuth) {
            return config;
          }

          if (hasAuthorizationHeader(config.headers)) {
            return config;
          }

          const token = getStoredToken();

          if (isTokenExpired(token)) {
            console.warn("[api] token expired (proactive) - dispatching auth:expired");
            clearStoredAuth();
            window.dispatchEvent(
              new CustomEvent("auth:expired", {
                detail: { message: "Sessão expirada" },
              }),
            );
          } else if (token) {
            config.headers = config.headers ?? {};
            (config.headers as Record<string, unknown>).Authorization = `Bearer ${token}`;
          } else if (config.headers && "Authorization" in config.headers) {
            delete (config.headers as Record<string, unknown>).Authorization;
          }
        } catch (e) {
          console.error("[api] request interceptor error", e);
        }
        return config;
      },
      (error) => Promise.reject(error),
    );

    this.axiosInstance.interceptors.response.use(
      (response) => response,
      (error) => {
        try {
          const status = error?.response?.status;
          const url = error?.config?.url;
          const data = error?.response?.data;
          const message = (data && (data.message || data.error || data.msg)) ?? "";
          const isSkipAuth = Boolean(error?.config?.skipAuth);

          if (status === 401 && !isAuthEndpoint(url) && !isSkipAuth) {
            console.log("[api] response error -> auth issue on non-auth endpoint", {
              status,
              url,
              message,
            });
            clearStoredAuth();
            window.dispatchEvent(
              new CustomEvent("auth:expired", {
                detail: { message: message || "Sessão expirada" },
              }),
            );
          } else if (status === 401 && isAuthEndpoint(url)) {
            console.log("[api] 401 on auth endpoint (likely normal during login):", {
              url,
              message,
            });
          }
        } catch (e) {
          console.error("[api] response interceptor error", e);
        }
        return Promise.reject(error);
      },
    );
  }

  async get<T>(endpoint: string, config?: AxiosRequestConfig): Promise<T> {
    try {
      const response: AxiosResponse<T> = await this.axiosInstance.get(
        endpoint,
        config,
      );
      return response.data;
    } catch (error) {
      console.error(`Erro ao fazer GET em ${endpoint}:`, error);
      throw error;
    }
  }

  async post<T>(
    endpoint: string,
    body: any,
    config?: AxiosRequestConfig,
  ): Promise<T> {
    try {
      const response: AxiosResponse<T> = await this.axiosInstance.post(
        endpoint,
        body,
        config,
      );
      return response.data;
    } catch (error) {
      console.error(`Erro ao fazer POST em ${endpoint}:`, error);
      throw error;
    }
  }

  async postRaw<T>(
    endpoint: string,
    body: any,
    config?: AxiosRequestConfig,
  ): Promise<AxiosResponse<T>> {
    return await this.axiosInstance.post(endpoint, body, config);
  }

  async patch<T>(
    endpoint: string,
    body: any,
    config?: AxiosRequestConfig,
  ): Promise<T> {
    try {
      const response: AxiosResponse<T> = await this.axiosInstance.patch(
        endpoint,
        body,
        config,
      );
      return response.data;
    } catch (error) {
      console.error(`Erro ao fazer PATCH em ${endpoint}:`, error);
      throw error;
    }
  }

  async put<T>(
    endpoint: string,
    body: any,
    config?: AxiosRequestConfig,
  ): Promise<T> {
    try {
      const response: AxiosResponse<T> = await this.axiosInstance.put(
        endpoint,
        body,
        config,
      );
      return response.data;
    } catch (error) {
      console.error(`Erro ao fazer put em ${endpoint}:`, error);
      throw error;
    }
  }

  async delete<T>(endpoint: string, config?: AxiosRequestConfig): Promise<T> {
    try {
      const response: AxiosResponse<T> = await this.axiosInstance.delete(
        endpoint,
        config,
      );
      return response.data;
    } catch (error) {
      console.error(`Erro ao fazer DELETE em ${endpoint}:`, error);
      throw error;
    }
  }
}

const api = new ApiConnector();
export default api;
